/**
 * LengthWarningContent Component
 *
 * Drawer-compatible version of length warning UI.
 * Can be used within drawers or dialogs.
 *
 * Story: UX-003, Task 5
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, FileText, Scissors, ListTree } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'

interface BlogPost {
  id: string
  title: string
  ai_metadata?: {
    phase: string
    word_count?: number
    length_warning?: boolean
  }
}

interface LengthWarningContentProps {
  post: BlogPost
  onDecision?: () => void
}

export function LengthWarningContent({ post, onDecision }: LengthWarningContentProps) {
  const queryClient = useQueryClient()
  const [selectedDecision, setSelectedDecision] = useState<'continue' | 'trim' | 'split_series' | null>(null)

  const wordCount = post?.ai_metadata?.word_count || 0

  // Length decision mutation
  const decisionMutation = useMutation({
    mutationFn: async (decision: 'continue' | 'trim' | 'split_series') => {
      if (!post) throw new Error('No post selected')
      return apiService.blog.aiWriter.lengthDecision(post.id, { decision })
    },
    onSuccess: (_, decision) => {
      const messages = {
        continue: 'Continuing with long-form content...',
        trim: 'Trimming content to target length...',
        split_series: 'Splitting into 2-part series...',
      }
      toast.success(messages[decision])
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      queryClient.invalidateQueries({ queryKey: ['blog-post', post.id] })
      setSelectedDecision(null)
      if (onDecision) onDecision()
    },
    onError: (error: any) => {
      console.error('Failed to process length decision:', error)
      toast.error(error?.response?.data?.detail || 'Failed to process decision')
    },
  })

  const handleDecision = (decision: 'continue' | 'trim' | 'split_series') => {
    setSelectedDecision(decision)
    decisionMutation.mutate(decision)
  }

  // Show loading state after decision is made
  if (decisionMutation.isPending && selectedDecision) {
    const messages = {
      continue: { emoji: '🖊️', text: 'Continuing with full article...', desc: 'Optimizing the long-form content for SEO and readability.' },
      trim: { emoji: '✂️', text: 'Trimming content...', desc: 'Intelligently condensing the article while preserving key points.' },
      split_series: { emoji: '📚', text: 'Creating Part 1...', desc: 'Splitting the content into a 2-part series.' },
    }
    const message = messages[selectedDecision]

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground">
        <div className="text-6xl mb-4 animate-pulse">{message.emoji}</div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">{message.text}</h3>
        <p className="text-muted-foreground text-center">{message.desc}</p>
      </div>
    )
  }

  if (!wordCount) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground">
        <div className="text-6xl mb-4">⏳</div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">Analyzing Content Length...</h3>
        <p className="text-muted-foreground text-center">
          The AI is checking the word count.
        </p>
      </div>
    )
  }

  const options = [
    {
      id: 'continue' as const,
      icon: FileText,
      title: 'Continue with Long Content',
      description: `Keep the full ${wordCount.toLocaleString()}-word article as-is. Best for comprehensive, in-depth content.`,
      color: 'text-blue-600 dark:text-blue-500',
      borderColor: 'border-blue-600 dark:border-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    },
    {
      id: 'trim' as const,
      icon: Scissors,
      title: 'Trim to Target Length',
      description: 'Intelligently condense the content to 1,500-1,800 words while preserving key points and SEO value.',
      color: 'text-orange-600 dark:text-orange-500',
      borderColor: 'border-orange-600 dark:border-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    },
    {
      id: 'split_series' as const,
      icon: ListTree,
      title: 'Split into 2-Part Series',
      description: 'Divide the outline into two complementary posts. Only Part 1 will be generated now.',
      color: 'text-purple-600 dark:text-purple-500',
      borderColor: 'border-purple-600 dark:border-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    },
  ]

  return (
    <div className="flex flex-col h-full bg-card text-card-foreground">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-amber-600" />
          Content Length Warning
        </h2>
        <p className="text-muted-foreground mt-1">
          The generated content is <span className="font-semibold text-amber-600">{wordCount.toLocaleString()} words</span>, which exceeds the recommended length of 1,800 words.
          Choose how you'd like to proceed:
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {options.map((option) => {
          const Icon = option.icon
          const isSelected = selectedDecision === option.id
          const isLoading = decisionMutation.isPending && isSelected

          return (
            <button
              key={option.id}
              onClick={() => handleDecision(option.id)}
              disabled={decisionMutation.isPending}
              className={cn(
                'w-full text-left p-4 rounded-lg border-2 transition-all',
                'hover:bg-accent hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed',
                isSelected && !isLoading
                  ? `${option.borderColor} ${option.bgColor}`
                  : 'border-border bg-background',
                decisionMutation.isPending && !isSelected && 'opacity-50'
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'mt-1 h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
                    isSelected ? option.color : 'text-muted-foreground',
                    isSelected ? option.bgColor : 'bg-muted'
                  )}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className={cn(
                    'font-semibold mb-1',
                    isSelected ? option.color : 'text-foreground'
                  )}>
                    {option.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Footer with note */}
      <div className="p-6 border-t border-border bg-muted/5">
        <p className="text-xs text-muted-foreground">
          <strong>Note:</strong> For most blog posts, 1,500-1,800 words is optimal for SEO and reader engagement.
          Longer articles may require more extensive promotion and have lower completion rates.
        </p>
      </div>
    </div>
  )
}
